import { useEffect, useState } from 'react';
import { AuroraBackdrop } from '@/gateway/layouts/auth/AuroraBackdrop';

const STAGE_MESSAGES = [
  'Creating your workspace…',
  'Provisioning workspace…',
  'Almost there — taking you in…',
];

/**
 * Full-screen transition shown after a successful workspace creation while the
 * browser performs a cross-subdomain navigation into /setup. The admin
 * favicon pulses inside concentric aurora rings so the gap never reads as a
 * blank page.
 */
export function WorkspaceTransitionOverlay({
  logoSrc = '/favicons/workspace.svg',
}: {
  logoSrc?: string;
}) {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [setTimeout(() => setStage(1), 900), setTimeout(() => setStage(2), 2200)];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="workspace-transition fixed inset-0 z-[100] flex items-center justify-center bg-[rgb(var(--background))]"
    >
      <AuroraBackdrop />

      <div className="relative flex flex-col items-center gap-8">
        <div className="relative h-40 w-40">
          <span
            className="workspace-transition-ring workspace-transition-ring-1"
            aria-hidden="true"
          />
          <span
            className="workspace-transition-ring workspace-transition-ring-2"
            aria-hidden="true"
          />
          <span
            className="workspace-transition-ring workspace-transition-ring-3"
            aria-hidden="true"
          />
          <div className="workspace-transition-logo absolute inset-0 m-auto flex h-24 w-24 items-center justify-center rounded-2xl bg-[rgb(var(--surface))] shadow-xl ring-1 ring-[rgb(var(--border))]">
            <img src={logoSrc} alt="" className="h-14 w-14" />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 text-center">
          <p
            className="workspace-transition-text text-xl font-medium text-slate-700 dark:text-slate-200"
            key={stage}
          >
            {STAGE_MESSAGES[stage]}
          </p>
          <div className="flex items-center gap-1.5" aria-hidden="true">
            <span className="workspace-transition-dot" style={{ animationDelay: '0ms' }} />
            <span className="workspace-transition-dot" style={{ animationDelay: '160ms' }} />
            <span className="workspace-transition-dot" style={{ animationDelay: '320ms' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
